import { apiClient, downloadFile } from './client';

const SESSIONS = '/api/reports/sessions';

/** Session report: generation, reading, translations, publishing and PDF export. */
export const reportApi = {
  /** Editable prompts, available models and their defaults. */
  getReportConfig: async () => {
    const response = await apiClient.get('/api/reports/config');
    return response.data;
  },

  /**
   * Start a report run. Options include `report_pipeline` ('v1' | 'v3'),
   * `in_depth_enabled`, `deeplinks_enabled`, `generate_qa` + `questions`,
   * `auto_send_faicon`, `auto_send_digwatch`, `llm_model` and `custom_prompts`.
   */
  generateSessionReport: async (sessionId, options = {}) => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/generate`, options);
    return response.data;
  },

  /** Deletes the current report so a new one can be generated. */
  regenerateReport: async (sessionId) => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/regenerate-report`);
    return response.data;
  },

  /** Recompute only the Q&A section; progress shows up as `qa_status` on the report. */
  regenerateSessionQa: async (sessionId, { questions = [], deeplinks_enabled = true } = {}) => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/regenerate-qa`, {
      questions,
      deeplinks_enabled: deeplinks_enabled !== false,
    });
    return response.data;
  },

  getSessionReportStatus: async (sessionId) => {
    const response = await apiClient.get(`${SESSIONS}/${sessionId}/report/status`);
    return response.data;
  },

  getSessionReport: async (sessionId, lang = null) => {
    const response = await apiClient.get(`${SESSIONS}/${sessionId}/report`, {
      params: lang ? { lang } : {},
    });
    return response.data;
  },

  publishToFaicon: async (sessionId, lang = 'en') => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/send-faicon`, null, { params: { lang } });
    return response.data;
  },

  /** `apiVersion` is the DigWatch preset: 'production' | 'v5' | 'v6'. */
  publishToWordPress: async (sessionId, lang = 'en', apiVersion = 'production') => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/send-digwatch`, null, {
      params: { lang, api_version: apiVersion },
    });
    return response.data;
  },

  downloadSessionReportPdf: async (sessionId, lang = 'en') => {
    const suffix = lang && lang !== 'en' ? `_${lang}` : '';
    await downloadFile(`${SESSIONS}/${sessionId}/report/pdf`, {
      params: { lang },
      fallbackName: `session_report_${sessionId}${suffix}.pdf`,
      mimeType: 'application/pdf',
    });
  },

  /**
   * One ZIP with a PDF per language, so the browser does not block several
   * downloads in a row. Without `langs` the backend includes every completed one.
   */
  downloadSessionReportPdfBundle: async (sessionId, langs = null) => {
    const response = await downloadFile(`${SESSIONS}/${sessionId}/report/pdf-bundle`, {
      params: Array.isArray(langs) && langs.length ? { langs: langs.join(',') } : {},
      fallbackName: `session_report_${sessionId}_bundle.zip`,
      mimeType: 'application/zip',
    });
    const header = (name) => (response.headers[name] || '').split(',').map((s) => s.trim()).filter(Boolean);
    return { included: header('x-pdf-languages-included'), failed: header('x-pdf-languages-failed') };
  },

  translateReport: async (sessionId, lang, modelType) => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/translate-report`, null, {
      params: { lang, ...(modelType ? { model_type: modelType } : {}) },
    });
    return response.data;
  },

  getReportLanguages: async (sessionId) => {
    const response = await apiClient.get(`${SESSIONS}/${sessionId}/report-languages`);
    return response.data;
  },

  deleteTranslation: async (sessionId, lang) => {
    const response = await apiClient.delete(`${SESSIONS}/${sessionId}/translations/${lang}`);
    return response.data;
  },

  cancelTranslation: async (sessionId, lang) => {
    const response = await apiClient.post(`${SESSIONS}/${sessionId}/translations/${lang}/cancel`);
    return response.data;
  },
};
